import React, { createContext, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../redux/actions';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const languageState = useSelector((state) => state.language);

  useEffect(() => {
    const storedLanguage = localStorage.getItem('i18nextLang');
    const browserLanguage = navigator.language.startsWith('ar') ? 'ar' : 'en';
    const initialLanguage = storedLanguage || browserLanguage;
    const direction = initialLanguage === 'ar' ? 'rtl' : 'ltr';
    
    // Update document direction
    document.body.dir = direction;
    document.documentElement.dir = direction;
    
    // Update i18n language
    i18n.changeLanguage(initialLanguage);
    
    // Update Redux state if needed
    if (initialLanguage !== languageState.language) {
      dispatch(setLanguage(initialLanguage, direction));
    }
  }, [dispatch, i18n, languageState.language]);

  const changeLanguage = (newLanguage) => {
    const direction = newLanguage === 'ar' ? 'rtl' : 'ltr';
    i18n.changeLanguage(newLanguage);
    dispatch(setLanguage(newLanguage, direction));
    document.body.dir = direction;
    document.documentElement.dir = direction;
  };

  const contextValue = {
    currentLanguage: languageState.language,
    direction: languageState.direction,
    isRTL: languageState.direction === 'rtl',
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;